import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { Navigate, useLocation, useNavigate } from 'react-router-dom';


interface ICountriesContext {
    recordCount: number
    search: ISearch
    countries: ICountry[]
    isLoading: boolean

    doSearch: (search: ISearch) => void
    handleChangePage: (event: any, page: number) => void
    updateSearch: (newSearch: ISearch) => void
    searchCountries: (searchRequest: ISearch) => Promise<void>
    deleteCountry: (id: string) => Promise<void>
}

export let countriesContext = React.createContext<ICountriesContext>({} as ICountriesContext);

let { Provider } = countriesContext;

interface ICountry {
    id: string
    code: string
    name: string
    locales: string[]
    currencyCode: string
    currencySymbol: string
    taxRateOverride?: number
}

export interface ISearch {
    fullText: string
    pageNr: number
    pageSize: number
}

export let CountriesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let location = useLocation();
    let navigate = useNavigate();

    let [isLoading, setLoading] = useState(true);


    let [countries, setCountries] = useState<ICountry[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearch>({
        fullText: '',
        pageNr: 0,
        pageSize: 20
    });

    useEffect(() => {
        if (location.state)
            processState(location.state);

    }, [location.state])


    if (!location.state)
        return <Navigate to={location.pathname} state={{ search: search }} replace={true} />


    let processState = (state: any) => {
        if (state) {

            setSearch({
                ...search,
                ...state
            });

            searchCountries({
                ...search,
                ...state
            });
        }
    }

    let doSearch = (values: ISearch) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        navigate(location.pathname, { state: request });
    }

    let updateSearch = (newSearch: ISearch) => {
        setSearch(newSearch);
    }

    let searchCountries = async (searchRequest: ISearch) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/Country/SearchCountries', searchRequest);

            setCountries(response.data.items);
            setTotalRecords(response.data.recordCount);
            setSearch({ ...searchRequest });

        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        navigate(location.pathname, { state: request });
    }

    let deleteCountry = async (id: string) => {
        if (window.confirm("Are you sure you want to delete the country?")) {
            try {
                await axios.delete(`/api/Country/DeleteCountry/${id}`)
                setCountries(countries.filter(country => country.id !== id));
                setTotalRecords(recordCount - 1);
                enqueueSnackbar("Country deleted", { variant: 'success' });
            } catch (error) { }
        }
    }


    return (
        <Provider value={{
            recordCount,
            search,
            countries,
            isLoading,

            doSearch,
            handleChangePage,
            updateSearch,
            searchCountries,
            deleteCountry
        }}>
            {children}
        </Provider>
    );
};